/**
 IMPORTANT!
 This service is an older version of the booking-availability-service submodule. Any updates must be made to both services and also updated in Diary Mobile.
 **/

import ObjectCopyService from "external-services/object-copy-service";
import DateUtilsService from "external-services/date-utils-service";
import {
  ITableItem,
  BookingMethods,
  TimeStatus,
  IBlockout,
  ITableItemAbstract, ServiceEventType, IBooking, BookingType, SECTION_ANY_ID
} from "shared-types/SharedTypes";
import { IScheduleTime, ILayoutMinimal, IDiarySchedule } from "shared-types/index";
import LayoutsService from 'external-services/layouts-service/layouts.service';
import { IBookingsByTable, IScheduleSubset, IBookingInfoAtTime, IBookingSubset, ILayoutSubset, ITableAvailableLookUp, IServiceItemSubset, IServiceItemSubsetMinimal } from './types';
import AdminService from 'external-services/diary-admin-service/admin.service';
import { uniq } from "lodash"
import moment from "moment";
import { cloneDeep } from 'lodash-es';

const NS = 'BookingAvailabilityService';

export const NO_TABLES_MSG = 'No tables available.';
export const CAPACITY_MSG_START = 'The capacity of T';
export const MIN_CAPACITY_MSG_START = 'The min capacity of T';
// use BookingAvailabilityService directly for static function references
export class BookingAvailabilityService {


  /**
   * Due to complexity of these public functions, private functions have been exposed for unit testing as well
   */
  static __getPrivateFunctionsForUnitTests() {
    return {
      getTotalTimes: this.getTotalTimes.bind(this),
      getBookingTimes: this.getBookingTimes.bind(this),
      getTableJoinsFromLayouts: this.getTableJoinsFromLayouts.bind(this),
      getMaxCoversAtTime: this.getMaxCoversAtTime.bind(this),
      formatDate: this.formatDate.bind(this),
      getBlockedSectionAndTableIdsAtTime: this.getBlockedSectionAndTableIdsAtTime.bind(this),
      getBookingIntervalFromService: this.getBookingIntervalFromService.bind(this),
      findBookingSlotsForSharedTables: this.findBookingSlotsForSharedTables.bind(this)
    }
  }

    /**
     * Returns a list of available booking times
     * @param  {Object}  schedule           The schedule you are booking in
     * @param  {Object}  serviceStartMin    The service you are booking for
     * @param  {Number}  requestDuration    How long you wish to book for
     * @param  {Number}  requestedPeople    The amount of people you wish to book for
     * @param  {Object}  bookingsByTable    A object of bookings by table see getBookingsByTable
     * @param  {String} preferredSectionId  The section id the guest would like to sit it
     * @param  {String} requiredTableId     The id of the table the guest would like to sit at
     * @param  {Booelan} allowPassTimes     Allow bookings from the past
     * @return {Array}                      Returns a list of booking times with status
     */
    static calulateBookingTimes(
        schedule: IScheduleSubset, service: IServiceItemSubset, layouts: ILayoutSubset[], requestDuration: number, requestedPeople: number,
        tables: ITableItem[], bookingsByTable: any, preferredSectionId: string, requiredTableId: string,
        allowPassTimes: boolean, bookingMethod: BookingMethods, includeEventBlockedSectionIds = false): IBookingInfoAtTime[] {
        if (!bookingsByTable) {
            return [];
        }

      /**
       * @todo: we are relying on momentJS a lot here to create dates (see dateStrToDateTime)
       * Perhaps this is creating a performance issue. Can we try to replace it with something else (like Luxon)?
       * Or perhaps we don't need to do so much Date object conversion and simply keep date strings where possible?
       * Severity: medium
       */

        // compensating for stupid triple 'o' variable name in diary
        const bookingInterval: number = service.boookingInterval || schedule.bookingInterval || schedule.boookingInterval;
        const startDate: Date = this.getStartTime(schedule.services[0]);
        const tablesJoins: ITableItem[] = this.getTableJoinsFromLayouts(layouts);

        const bookingInfoAtTimeList: IBookingInfoAtTime[] = [];
        const bookingTimes: Date[] = this.getBookingTimes(startDate, service, bookingInterval);

        bookingTimes.forEach(time => {
          const bookingInfo: IBookingInfoAtTime = this.getBookingInfoAtTime(
            schedule, time, startDate, service, tablesJoins, requestDuration, requestedPeople, tables,
            bookingsByTable, preferredSectionId, requiredTableId, bookingMethod, includeEventBlockedSectionIds
          );
          bookingInfoAtTimeList.push(cloneDeep(bookingInfo));
        });

        return bookingInfoAtTimeList;
    }

    private static getTotalTimes(startDate: Date, serviceStartDate: Date, service: IServiceItemSubsetMinimal, bookingInterval: number): number {
      const serviceStartMin: number = DateUtilsService.dateTimeNum(startDate, serviceStartDate);
      const serviceEndMin: number = DateUtilsService.dateTimeNum(startDate, service.endTime);
      return Math.ceil((serviceEndMin - serviceStartMin) / bookingInterval);
    }

    // create a list of possible booking times
    private static getBookingTimes(startDate: Date, service: IServiceItemSubset, bookingInterval: number): Date[] {
      const serviceStartDate: Date = service.firstBookingTime || this.getStartTime(service);
      const totalTimes: number = this.getTotalTimes(startDate, serviceStartDate, service, bookingInterval);

      const bookingTimes: Date[] = [];
      for (let i = 0; i < totalTimes; i++) {
        const time: Date = new Date(serviceStartDate.getTime());
        const minsToAdd: number = i * bookingInterval;
        time.setMinutes(time.getMinutes() + minsToAdd);
        time.setSeconds(0);
        time.setMilliseconds(0);
        bookingTimes.push(time);
      }
      return bookingTimes;
    }

    private static getTableJoinsFromLayouts(layouts: ILayoutSubset[]): ITableItem[] {
      let tablesJoins: ITableItem[] = [];
      layouts.forEach(layout => {
        if (layout.tablesJoins?.length) {
          let _tablesJoins = layout.tablesJoins;
          if (!Object.isExtensible(_tablesJoins)) {
            // somehow redux is setting layout.tablesJoins and so it can't be extended
            _tablesJoins = cloneDeep(_tablesJoins);
          }
          tablesJoins = tablesJoins.concat(_tablesJoins);
        }
      });

      return tablesJoins;
    }

    static checkBookingsAtTime(
        time: Date, bookingDate: Date, schedule: IScheduleSubset, service: IServiceItemSubset, layouts: ILayoutMinimal[],
        requestDuration: number, requestedPeople: number, tables: ITableItemAbstract[], bookingsByTable: any,
        preferredSectionId: string, requiredTableId: string, allowPassTimes: boolean, bookingMethod: BookingMethods,
        includeEventBlockedSectionIds = false
    ): IBookingInfoAtTime {
        // work out the total time
        const startDate: Date = this.getStartTime(schedule.services[0]);
        const tablesJoins: ITableItem[] = this.getTableJoinsFromLayouts(layouts);

        return this.getBookingInfoAtTime(schedule, time, startDate, service, tablesJoins, requestDuration,
          requestedPeople, tables, bookingsByTable, preferredSectionId, requiredTableId, bookingMethod,
          includeEventBlockedSectionIds);
    }

    // work out the capacity of the restaurant at this time
    private static getMaxCoversAtTime(time: Date, service: IServiceItemSubset): number {

      let maxCovers: number = service.maxCoversPerInterval;

      if (service.enableCustomPaxPerIntervals === true) {
        const formattedTime: string = this.formatDate(time);

        const targetInterval = service.intervalsCustom.find(intv => intv.times === formattedTime);
        if (targetInterval) {
          if (targetInterval.values || targetInterval.values === 0) {
            maxCovers = targetInterval.values ? targetInterval.values as number : 0;
          }
        }
      }

      return maxCovers;
    }


    private static getBlockedSectionAndTableIdsAtTime(time:Date, blockouts: IBlockout[]): { blockedSectionIds: string[], blockedTableIds: string[] } {
      let blockedSectionIds: string[] = [];
      let blockedTableIds: string[] = [];
      blockouts.forEach(blockout => {
        if (DateUtilsService.isInBetweenDates(time, blockout.startDateTime, blockout.endDateTime)) {
          // the time overlaps a blockout
          blockedSectionIds = blockedSectionIds.concat(blockout.blockedSections);
          blockedTableIds = blockedTableIds.concat(blockout.blockedTables.map(table => table._id));
        }
      });

      return { blockedSectionIds, blockedTableIds }
    }

    private static getBookingIntervalFromService(service: IServiceItemSubset): number {
      // in case the service is an event type, intervalDuration is informed by the property "boookingInterval" (as it is, with misspelling)
      const bookingIntervalAsPossibleString = (service.type.toLowerCase() === ServiceEventType.Event.toLowerCase()
        ? service.boookingInterval // triple 'o' is a typo we must learn to live with
        : service.intervalsCustom && service.intervalsCustom.length ? service.intervalsCustom[0].intervalDuration : 0);

      const bookingInterval = typeof bookingIntervalAsPossibleString === "string" ? parseInt(bookingIntervalAsPossibleString) : bookingIntervalAsPossibleString;

      return bookingInterval;
    }

    private static getBookingInfoAtTime(
        schedule: IScheduleSubset, time: Date, startDate: Date, service: IServiceItemSubset, tablesJoins: ITableItem[],
        requestDuration: number, requestedPeople: number, tables: ITableItemAbstract[], bookingsByTable: any,
        preferredSectionId: string, requiredTableId: string, bookingMethod: BookingMethods,
        includeEventBlockedSectionIds: boolean
    ): IBookingInfoAtTime {

        let errorMgs: string[] = [];
        let warningMsgs: string[] = [];
        // work out the total time
        startDate = this.getStartTime(schedule.services[0]);
        const serviceStartMin: number = DateUtilsService.dateTimeNum(startDate, this.getStartTime(service));
        const serviceEndMin: number = DateUtilsService.dateTimeNum(startDate, this.getStartTime(service, true));
        // work out the capacity of the restaurant at this time
        const maxCovers: number = this.getMaxCoversAtTime(time, service);

        // Check what bookings are on at this time
        let coversReached = 0;
        let coversReachedAtTime = 0;
        const bookingsAtTime: IBookingSubset[] = [];
        const bookingsStartingAtTime: IBookingSubset[] = [];
        // work out which tables are appropriate
        const requestedStartMin: number = Math.ceil(DateUtilsService.dateTimeNum(startDate, time));
        const requestedEndMin: number = requestedStartMin + requestDuration;

        const endTime: Date = new Date(time.getTime());
        endTime.setMinutes(endTime.getMinutes() + requestDuration);

        let vaildTablesAtTime: ITableItem[] = [];
        let freeTablesAtTime: ITableItem[] = [];
        let invalidTablesAtTime: ITableItem[] = [];

        const blocked = this.getBlockedSectionAndTableIdsAtTime(time, schedule.blockouts);
        const blockedTableIds = blocked.blockedTableIds;
        const blockedSectionIds = blocked.blockedSectionIds;
        let eventBlockedSectionIds: string[] = [];

        // @todo: reinstate when events working for ABC back end
        if (includeEventBlockedSectionIds) {
          eventBlockedSectionIds = this.getBlockedSectionIdsOnEvents(service._id, time, endTime, schedule.services);
        }

        const totalTables = tables.length;
        const tableAvailableLookUp: any = {}; // of ITableAvailableLookUp
        // @todo: requiredTable needs to have a type (when converting to TS) that extends ITableItem to include `available` and `validTable`. Severity: medium
        let requiredTable;
        let validTable;

        const bookingInterval = this.getBookingIntervalFromService(service);

        const bookingSlots: number[] = [];
      // configure the number of booking slots
        if (requestedEndMin > 0 && bookingInterval > 0) {
            const serviceDuration = service.duration || service.bookingDuration; // could be either (both values mean same thing - duration of the service)
            const reqIntervals = (serviceDuration/bookingInterval) + 1;

            for (let i = 0; i < reqIntervals; i++) {
                const requestSliceEnd: number = requestedStartMin + (bookingInterval * (i+1));
                if (requestSliceEnd <= requestedEndMin) {
                    bookingSlots.push(0);
                }
                else {
                    break;
                }
            }
        }

        for (let f = totalTables - 1; f >= 0; f--) {
            const table: ITableItem = tables[f];
            table.problemReasons = [];
            const tableId: string = table._id;
            // table info in bookingsByTable is not newest
            const tableObj = bookingsByTable[tableId]; // @todo: determine type

            // NBI-4750, the table info in booking is not newest, should we check with backend or where we get the table info?
            tableObj?.bookings.forEach((booking: IBooking) => {
              // not sure why this is immutable, so this is just a bit of a bandaid fix (may have been fixed in new BookingAvailabilityService)
              if (!Object.isExtensible(booking)) {
                booking = cloneDeep(booking);
              }
              booking.tables = booking.tables.map((bookingTable) => bookingTable._id === tableId ? cloneDeep(table) : bookingTable);
            })
            const tableBookings: IBookingSubset[] = tableObj ? tableObj.bookings : [];
            // let tableCoversReached = 0;

            validTable = false;

            // cleans bookingSlot array if the table type is shared
            if (table.shared) {
                bookingSlots.forEach((item, j) => {
                    bookingSlots[j] = 0;
                });
            }

            // does the table have the right amout of capacity
            // Is the table in the right section
            const isByPhone = bookingMethod === BookingMethods.phone;
            const isByWalkin = bookingMethod === BookingMethods.walkin;
            if (
                (!isByPhone || (!isByWalkin && !table.walkinOnly)) &&
                (table.capacity >= requestedPeople || table.shared) &&
                (requestedPeople >= table.minCapacity)

              /**
               * If a specific section is selected, this will make tables in other sections orange ("free", but not "valid")/
               */
              && (preferredSectionId === 'any' || preferredSectionId === table.sectionId)

              /**
               * @todo: If diary timeline interval is clicked, this will correspond to the table dropdown next to the status bar,
               * which selects the clicked table. That then makes other tables orange in the same section, unless the table dropdown
               * is set to auto. This is a bit confusing and I think should be removed.
               * Severity: medium
               */
              && (!requiredTableId || requiredTableId === 'auto' || requiredTableId === table._id)
            ) {
                validTable = true;
            } else {
              if(preferredSectionId !== SECTION_ANY_ID && preferredSectionId !== table.sectionId) {
                table.problemReasons.push(`Table is not in the selected section`);
              }
              if (!table.shared && table.capacity < requestedPeople) {
                table.problemReasons.push(`Too many people on table (only space for ${table.capacity} people)`);
              }
              if (requestedPeople < table.minCapacity) {
                table.problemReasons.push(`Not enough people on table (minimum is ${table.minCapacity})`);
              }

            }

            // table is approiate
            let available = false;
            // does the table have any bookings at all
            if (!tableBookings.length) {
                available = true;
            }

            // loop over the booking to find a gap
            const totalTableBookings = tableBookings.length;
            for (let t = 0; t < totalTableBookings; t++) {
                const booking: IBookingSubset = tableBookings[t];

                const bookingEnd: Date = booking.bookingEnd || DateUtilsService.getBookingEnd(booking);

                booking.bookingEnd = bookingEnd;
                const bookingStartMin: number = Math.ceil(DateUtilsService.dateTimeNum(startDate, booking.time));
                const bookingEndMin: number = Math.ceil(DateUtilsService.dateTimeNum(startDate, bookingEnd));

                // if the table is valid check for free space
                // can the request booking fit before this one
                if (t === 0 && requestedStartMin < bookingStartMin && requestedEndMin <= bookingStartMin) {
                    available = true;
                }
                // can the request booking fit after this one
                const startGap = bookingEndMin;
                let endGap: number = null;
                if (t < tableBookings.length - 1) {
                    // change the gap end start of the next booking if there is one
                    endGap = DateUtilsService.dateTimeNum(startDate, tableBookings[t + 1].time);
                }

                if (requestedStartMin >= startGap) {
                    const noGapBetweenEndAndStart = startGap === endGap;
                    if (endGap && !noGapBetweenEndAndStart) {
                        if (requestedEndMin <= endGap) {
                            available = true;
                        }
                    } else if (endGap === null) {
                        available = true;
                    }
                }

                let index: number;
                if (bookingStartMin <= requestedStartMin && requestedStartMin < bookingEndMin) {
                    // check booking is not a copy
                    index = bookingsAtTime.map(function (e) {
                        return e._id;
                    }).indexOf(booking._id);
                    // make sure the booking is not already in the array
                    if (index === -1 && booking) {

                        // Added  hot fix for display triggering custom itnervals

                        if (booking.serviceId == service._id) {
                            coversReached += booking.people;
                        }
                        bookingsAtTime.push(booking);
                    }
                }

                if (bookingStartMin === requestedStartMin) {
                    // check booking is not a copy
                    index = bookingsStartingAtTime.map(function (e) {
                        return e._id;
                    }).indexOf(booking._id);
                    // make sure the booking is not already in the array
                    if (index === -1 && booking) {
                        if (booking.serviceId == service._id) {
                            coversReachedAtTime += booking.people;

                            bookingsStartingAtTime.push(booking);
                        }
                    }
                }
            }

            if (!available) {
              table.problemReasons.push(`Table is already booked`);
            }
            // Find bookingSlots for shared tables
            this.findBookingSlotsForSharedTables(
              {
                tableBookings,
                requestedTime: time,
                requestedDuration: requestDuration,
                bookingSlots
              }
            )

            // Gets the maximum of people that will be booked on any interval
            const maxCoversOnSlots: number = (bookingSlots && bookingSlots.length > 0 ? Math.max(...bookingSlots) : 0);

            // check if the table is shared and it has enough covers left
            if (table.shared && ((table.capacity - maxCoversOnSlots) >= requestedPeople)) {
                available = true;
            } else if (table.shared) {
                validTable = false;
                table.problemReasons.push(`Too many people on shared table (only space for ${table.capacity - maxCoversOnSlots} people)`);
            }

            // make sure table is not blocked
            let blockedTable = false;
            if (available && blockedTableIds.indexOf(table._id) !== -1 || (blockedSectionIds.indexOf(table.sectionId) !== -1)) {
                available = false;
                blockedTable = true;
                table.problemReasons.push(`Table has a blockout on it`);
            }

            // NBI-7693 - blocked times due to overlap with another event - show different message
            if(eventBlockedSectionIds.indexOf(table.sectionId) !== -1) {
              available = false;
              blockedTable = true;
              table.problemReasons.push(`Table is unavailable due to a future event`);
            }

            // make sure the table is not for walk-ins only
            let walkinTable = false;
            if (available && bookingMethod !== BookingMethods.walkin && table.walkinOnly) {
                available = false;
                walkinTable = true;
                table.problemReasons.push(`Table is set to walk-in only`);
            }

            if (available && validTable && !blockedTable) {
                vaildTablesAtTime.push(table);
            }

            // need to include the invalid tables too, so that blocked out table messaging is still attached to each time
            if (!available || !validTable || blockedTable) {
              invalidTablesAtTime.push(table);
            }

            if (available) {
                freeTablesAtTime.push(table);
            }
            tableAvailableLookUp[table._id] = {
                table: table,
                available: available,
                walkinTable: walkinTable
            } as ITableAvailableLookUp;

            if (requiredTableId === table._id) {
                requiredTable = ObjectCopyService.angularSafeCopy(table);
                requiredTable.validTable = validTable;
                requiredTable.available = available;
            }
        }

        // work out what joins are free
        const totalJoinTables = tablesJoins ? tablesJoins.length : 0;
        const vaidJoinsAtTime: ITableItemAbstract[] = [];
        for (let j = totalJoinTables - 1; j >= 0; j--) {
            validTable = false;
            const tablesJoin = tablesJoins[j];

            const isByPhone = bookingMethod === BookingMethods.phone;
            const isByWalkin = bookingMethod === BookingMethods.walkin;
            if ((!isByPhone || (!isByWalkin && !tablesJoin.walkinOnly)) && tablesJoin.capacity >= requestedPeople && requestedPeople >= tablesJoin.minCapacity) {
                validTable = true;
            }
            if (validTable) {
                let joinAvailable = true;
                const totalJoins = tablesJoin.tables.length;
                const tablesList: ITableItemAbstract[] = [];
                const tableNames: string[] = [];
                for (let c = 0; c < totalJoins; c++) {
                    const tableLookUp: ITableAvailableLookUp = tableAvailableLookUp[tablesJoin.tables[c]];
                    if (tableLookUp) {
                        tablesList.push(tableLookUp.table);
                        tableNames.push(tableLookUp.table.name);
                        // if table unavailable and not because it's reserved for walkins, or it's not in a preferred section then the join is unavailable
                        if (
                          (!tableLookUp.available && !tableLookUp.walkinTable) ||
                          (preferredSectionId !== 'any' && preferredSectionId !== tableLookUp.table.sectionId)
                        ) {
                            // one of the tables are not available
                            joinAvailable = false;
                            break;
                        }
                    }

                }
                if (joinAvailable) {
                    // note: this may get called 3 or 4 times before getting added
                    tablesJoin.tablesList = tablesList;
                    tablesJoin.name = tableNames.sort().join('-');
                    vaidJoinsAtTime.push(tablesJoin);
                }
            }
        }

        let status = TimeStatus.available;

        if (vaildTablesAtTime.length === 0 && vaidJoinsAtTime.length === 0) {
            errorMgs.push(NO_TABLES_MSG);
        }
        if (errorMgs.length) {
            status = TimeStatus.unavailable;
        }
        if (requiredTable) {
            errorMgs = [];
            warningMsgs = [];
            if (!requiredTable.available) {
                errorMgs.push('T' + requiredTable.name + ' is not available.');
            }
            if (!requiredTable.validTable) {
                if (requestedPeople > requiredTable.capacity) {
                    warningMsgs.push(CAPACITY_MSG_START + requiredTable.name + ' is ' + requiredTable.capacity + '.');
                }
                if (requestedPeople < requiredTable.minCapacity) {
                    warningMsgs.push(MIN_CAPACITY_MSG_START + requiredTable.name + ' is ' + requiredTable.minCapacity + '.');
                }
            }
        }


        if ((maxCovers || maxCovers === 0) && (coversReachedAtTime + requestedPeople) > maxCovers) {


            warningMsgs.push('You have reached your maximum covers at this time');
        }
        if (requestedEndMin > serviceEndMin) {
            //  warningMsgs.push('This booking ends after ' + service.name + ' has finished.');
        }

      /**
       * In Diary 'lastBookingTime' is present, but on Group Widget (ABC) only `times` is present
       */
      if (!service.lastBookingTime && !service.times) {
        console.warn(NS, 'Could not determine last booking time of service', service, 'So cannot check if booking starts after this.');
      } else {
        const lastBookingTime: Date = service.lastBookingTime || DateUtilsService.dateStrToDateTime( service.times[service.times.length-1].time );
        if (time.getTime() > lastBookingTime.getTime()) {
            warningMsgs.push('This booking starts after your last booking time ' + DateUtilsService.dateToTimeStr(lastBookingTime));
            // warningMsgs.push('This booking starts after your last booking time ' + this.$filter('date')(service.lastBookingTime, 'hh:mm a'));
        }
      }

        let open = true;
        if (requestedStartMin < serviceStartMin) {
            open = false;
        } else if (requestedStartMin > serviceEndMin) {
            open = false;
        }
        if (!open) {
            warningMsgs = [];
            errorMgs = ['You are closed at this time.'];
        }
        if (service.maxBookingsPerInterval && service.maxBookingsPerInterval !== 0 && bookingsStartingAtTime.length >= service.maxBookingsPerInterval) {
            warningMsgs.push('You have reached your maximum bookings at this time');
        }
        if (!open) {
            vaildTablesAtTime = [];
            freeTablesAtTime = [];
        }
        // add to the list of bookings times

        return {
            time,
            maxCovers,
            coversReached: coversReachedAtTime,
            bookingsAtTime,
            vaildTablesAtTime,
            freeTablesAtTime,
            vaidJoinsAtTime,
            invalidTablesAtTime, // introduced in booking-availability-service submodule
            status, // @todo: make of type TimeStatus (when converting to TS)
            errorMgs,
            warningMsgs,
            open,
            service: service.name,
        };
    }

    /**
     * Give a list of tables and bookings this will map them to bookings by table
     * @param  {Array} bookings
     * @param  {Array} tables
     * @return {Object} an object of tables with bookings array
     */
    static getBookingsByTable(bookings: IBookingSubset[]): any {
        // work out date
        // group bookings by table
        const bookingsByTable: any = {};
        const bookingsLength = bookings.length;
        for (let b = 0; b < bookingsLength; b++) {
            const booking = bookings[b];
            // set the booking end time

            if (!booking.status) {
                console.warn(NS, 'Detected booking without a status. Treating it as cancelled. This may be due to "dirty" data.', booking);
            }
            if (booking.status && booking.status.statusType !== 'cancelled') {
                // add the booking to its table
                if (booking.tables) {
                    for (let i = booking.tables.length - 1; i >= 0; i--) {
                        const table = booking.tables[i];
                        if (table) {
                            if (!bookingsByTable[table._id]) {
                                bookingsByTable[table._id] = {
                                    table: table,
                                    bookings: []
                                };
                            }
                            bookingsByTable[table._id].bookings.push(booking);
                        }
                    }
                }
            }
        }
        return bookingsByTable;
    }

  private static findBookingSlotsForSharedTables(
    {
      requestedTime,
      requestedDuration,
      tableBookings,
      bookingSlots
    }: {
      requestedTime: Date,
      requestedDuration: number,
      tableBookings: IBookingSubset[],
      bookingSlots: number[]
    }
  ): number[] {
    const sharedTableBooking = tableBookings.filter((booking) => booking.tables.some((t) => t.shared))
    if (sharedTableBooking.length === 0) return bookingSlots;
    const bookingStartTimeTimeStamps = sharedTableBooking.map((v) => v.time.valueOf());
    const bookingsEndTimeTimeStamps = sharedTableBooking.map((v) => v.bookingEnd.valueOf());
    // Just to ensure the correct order of timestamp which is a big help for booking iteration below
    const booksTime = uniq(bookingStartTimeTimeStamps.concat(bookingsEndTimeTimeStamps).sort((a,b) => a - b));
    const requestedEndTime = moment(requestedTime).add(requestedDuration, 'minutes');
    // New Booking
    const startTimeNewRequest = requestedTime.valueOf();
    const endTimeNewRequest = requestedEndTime.valueOf();
    // To prevent undefined value from i + 1, length of iteration should be minus 2
    for(let i = 0; i <= booksTime.length - 2; i++) {
      const firstTimeStamp = booksTime[i];
      const secondTimeStamp = booksTime[i+1];
      let people = 0;
      // iterate bookings to find how many people booked for this duration (btw firstTimeStamp and secondTimeStamp)
      sharedTableBooking.forEach((v) => {
        const startTimeStamp = v.time.valueOf();
        const endTimeStamp = v.bookingEnd.valueOf();
        const bookedPeople = v.people;
        if (
          firstTimeStamp <= startTimeStamp && startTimeStamp < secondTimeStamp ||
          firstTimeStamp < endTimeStamp && endTimeStamp <= secondTimeStamp ||
          startTimeStamp < firstTimeStamp && secondTimeStamp < endTimeStamp
        ) {
          people += bookedPeople
        }
      })
      // Only fill up block ( which is covered by new booking ) with people
      // For example: original blocks: [0,0,0,0,0], but only 3rd block, 4th block, 5th block position cover new booking
      if (
        firstTimeStamp <= startTimeNewRequest && startTimeNewRequest < secondTimeStamp ||
        firstTimeStamp < endTimeNewRequest && endTimeNewRequest <= secondTimeStamp ||
        startTimeNewRequest < firstTimeStamp && secondTimeStamp < endTimeNewRequest
      ) {
        bookingSlots.push(people)
      }
    }
    return bookingSlots
  }

    // make sure the table is not blocked by an event
    private static getBlockedSectionIdsOnEvents(currentServiceId: string, time: Date, endTime: Date, services: IServiceItemSubsetMinimal[]): string[] {
        let result: string[] = [];
        for (let e = services.length - 1; e >= 0; e--) {
            const s = services[e];
            // Check to make sure the service is an event and it does not equal to the current service
            if (s.type && s.type === 'event' && s._id !== currentServiceId) {
                if (endTime.getTime() > s.startTime.getTime() && time.getTime() < s.endTime.getTime()) {
                    // the times overlap
                    const sectionsIdUsed = s.sectionLayouts.map(({sectionId}) => sectionId);
                    result = result.concat(sectionsIdUsed);
                }
            }
        }

        return result;
    }

    private static getStartTime(service: {endTime?: Date, startTime?: Date, times?: IScheduleTime[]}, useEndTime = false): Date {
        const startDate: Date = (useEndTime ? service.endTime : service.startTime)
            || DateUtilsService.dateStrToDateTime(
                useEndTime
                    ? service.times[service.times.length-1].time
                    : service.times[0].time
            );
        return startDate;
    }

    private static formatDate(date: Date): string {
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const minutesAsString = minutes < 10 ? '0' + minutes : minutes;
        const strTime = hours + ':' + minutesAsString + ' ' + ampm;
        return strTime;
    }

  private static async checkAvailableTables(booking: IBooking, schedule: IDiarySchedule, serviceIds: string[], bookingsByTable: IBookingsByTable, timeNow: Date): Promise<IBooking> {
    const serviceIndex = serviceIds.indexOf(booking.serviceId);

    // service may have been deleted, but booking is still in wait list
    if (serviceIndex === -1) {
      booking.tablesAvaiable = false;
      return booking;
    }

    const service = schedule.services[serviceIndex];
    const layoutsIds: string[] = service.sectionLayouts.map(({layoutId}) => layoutId);
    // get the tables for this service
    const [tablesRes, layouts] = await Promise.all([
      LayoutsService.getByLayouts(layoutsIds, timeNow),
      LayoutsService.getLayouts(layoutsIds, timeNow)
    ]);
    const tables = tablesRes.tablesArray;
    const bookingTime = timeNow > booking.time? timeNow : booking.time;
    const preferredSectionId = (booking.preferredSectionId) ? booking.preferredSectionId : 'any';
    const availableTables = this.checkBookingsAtTime(bookingTime, bookingTime, schedule, service, layouts as unknown as ILayoutMinimal[], +booking.duration, booking.people, tables, bookingsByTable, preferredSectionId, undefined, undefined, undefined);
    booking.tablesAvaiable = !!(availableTables.vaildTablesAtTime.length || availableTables.vaidJoinsAtTime.length);
    return booking;
  }

  private static isBookingInWaitList(booking: IBooking): boolean {
      return (booking.status && booking.status.statusType !== 'cancelled') &&
        (!booking.tables || booking.tables.length === 0 || booking.bookingType === BookingType.StandbyList)
  }

  static async checkWaitListForAvailableTables(
    schedule: IDiarySchedule, bookings: IBooking[] = [], bookingsByTable: IBookingsByTable
  ): Promise<IBooking[]> {
    const waitListBookings = bookings.filter((booking: IBooking) => this.isBookingInWaitList(booking));
    if (waitListBookings.length) {
      const serviceIds = schedule.services.map(service => service._id);
      const timezoneId = AdminService.getVenueTimezoneId();
      const timeNow = DateUtilsService.getVenueTime(timezoneId);
      // There are bookings on the wait list that are ready to be seated.
      await Promise.all(waitListBookings.map(
        (booking: IBooking) => this.checkAvailableTables(booking, schedule, serviceIds, bookingsByTable, timeNow)
      ));
      return bookings;
    }
    return null;
  }

  static setSeatNowTime(isDateToday: boolean, timezoneId:string): Date {
    const seatNowTime = DateUtilsService.getVenueTime(timezoneId);
    seatNowTime.setSeconds(0);
    seatNowTime.setMilliseconds(0);
    return seatNowTime;
  }

  static isBookingTimeInList(bookingTimes:IBookingInfoAtTime[], currentBookingTime: Date): boolean {
    const bookingTimesList = bookingTimes.map(
      (bookingTime) => DateUtilsService.dateToTimeStr(bookingTime.time, 'hh:mm A')
    );
    const bookedTimeStr = DateUtilsService.dateToTimeStr(currentBookingTime, 'hh:mm A');
    return bookingTimesList.indexOf(bookedTimeStr) > -1;
  }


  static getSelectedBookingTimeFromList(bookingTimes:IBookingInfoAtTime[], currentBookingTime: Date): {selectedTimeId: number | undefined, selectedTime: IBookingInfoAtTime | undefined} {
      let index, item;
      bookingTimes.some((e, i) => {
        if (e.time.getTime() === currentBookingTime.getTime()) {
          index = i;
          item = e;
          return true
        }
        return false
      })
      return {selectedTimeId: index, selectedTime: item}
  }
}

